// Sprint 17.3 — Admin Forecast page.
//
// Replaces the ComingSoon stub. Renders the latest forecast_snapshot
// (or an empty state if none exists yet). Run Scraper button hits
// POST /api/admin/forecast/scrape; the response is the fresh
// snapshot. Generate Forecast is wired in 17.4.
//
// One file with inline sub-components on purpose — keeps the data
// flow readable, matches AdminHome's pattern, avoids over-fragmenting
// what's essentially one page.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiFetch } from '../../auth';
import { useView } from '../../shells/ViewContext';
// ForecastSheet import removed in 17.12 — modal lives on Forecast page now.
import ForecastSettings from './ForecastSettings';
import ForecastHistory from './ForecastHistory';
import './Forecasting.css';
import ResnTable from './ResnTable';
import RecentChanges from './RecentChanges';
import SyncBadge from '../shared/SyncBadge';
import KpiCard from '../shared/KpiCard';   // Sprint 24.5: the tile moved to shared/ so Housekeeping uses the very same one
import { buildRoomCondition, fraction } from './roomCondition';
import RoomConditionPanel from './RoomConditionPanel';
import { RESN_TABS, DEFAULT_RESN_TAB, tabPredicate } from './resnTabs';


// ── Sprint 17.9 inline SVG icons ───────────────────────────
// Stroke uses currentColor so each icon matches its button's text.

const IconRefresh = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12a9 9 0 1 1-3.2-6.9" />
    <polyline points="21 4 21 10 15 10" />
  </svg>
);

const IconSend = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M22 2 11 13" />
    <path d="M22 2 15 22 11 13 2 9z" />
  </svg>
);

const IconClock = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <polyline points="12 7 12 12 15 14" />
  </svg>
);

const IconGear = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
  </svg>
);

const IconDocument = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="15" y2="17" />
  </svg>
);

// Sprint 17.10 — KPI card icons + back chevron.

const IconBack = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const IconBroom = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 4 9 10" />
    <path d="m19 8-3-3" />
    <path d="M9 10 4 21l11-5z" />
    <path d="M7 17h5" />
  </svg>
);

const IconBriefcase = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="7" width="18" height="13" rx="2" />
    <path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const IconExit = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
    <polyline points="10 17 15 12 10 7" />
    <line x1="15" y1="12" x2="3" y2="12" />
  </svg>
);

const IconBed = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 17v-5a2 2 0 0 1 2-2h11a4 4 0 0 1 4 4v3" />
    <path d="M2 17h20" />
    <path d="M2 20v-3" />
    <path d="M22 20v-3" />
    <circle cx="7.5" cy="12.5" r="1.5" />
  </svg>
);

const IconSparkle = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3v4" />
    <path d="M12 17v4" />
    <path d="M3 12h4" />
    <path d="M17 12h4" />
    <path d="m5.6 5.6 2.8 2.8" />
    <path d="m15.6 15.6 2.8 2.8" />
    <path d="m5.6 18.4 2.8-2.8" />
    <path d="m15.6 8.4 2.8-2.8" />
  </svg>
);

const IconUsers = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M22 21v-2a4 4 0 0 0-3-3.9" />
    <path d="M16 3.1a4 4 0 0 1 0 7.8" />
  </svg>
);

// Sprint 18.1 — moon for "Staying Tonight" KPI; alert triangle for
// "No Room Assigned" KPI (needs admin review).
const IconMoon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

const IconAlertTriangle = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9"  x2="12"   y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// SVG progress ring. `pct` 0–100; while indeterminate (no real
// signal from the server), the parent fakes it from elapsed time.
// Sprint 17.10 — default size 16 so it swaps cleanly with the
// 16px IconRefresh (the button width doesn't jump when scraping
// starts/stops).
const ProgressRing = ({ pct = 0, size = 16, stroke = 2.2 }) => {
  const r = (size - stroke) / 2;
  const C = 2 * Math.PI * r;
  const offset = C * (1 - Math.min(100, Math.max(0, pct)) / 100);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <circle cx={size/2} cy={size/2} r={r}
        fill="none" stroke="currentColor" strokeOpacity="0.25" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r}
        fill="none" stroke="currentColor" strokeWidth={stroke}
        strokeDasharray={C} strokeDashoffset={offset}
        strokeLinecap="round"
        transform={`rotate(-90 ${size/2} ${size/2})`}
        style={{ transition: 'stroke-dashoffset 0.25s linear' }}
      />
    </svg>
  );
};


// ── Formatters ─────────────────────────────────────────────

const fmtTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const fmtDate = (val) => {
  if (!val) return '—';
  // Accepts either 'YYYY-MM-DD' (e.g. payload.forecastDate) or an ISO
  // timestamp (e.g. forecast_snapshot.forecast_date, which pg
  // serialises as '2026-06-04T00:00:00.000Z'). Slice to the date
  // portion first, then build a Date with explicit local midnight so
  // toLocaleDateString doesn't shift it by timezone.
  const s = String(val).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return s;
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString([], {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  });
};

const ACTION_LABEL = {
  checkoutClean:   'Check-out clean',
  stayoverService: 'Stayover service',
  none:            '—',
};


// ── Sub-components ─────────────────────────────────────────

// Sprint 17.10 KpiCard. Layout per the user's reference mockup:
//
//   [ICON]  Label
//           PRIMARY  secondary
//           sublabel
//
// `primary` is the big foreground number (e.g. "16 not-yet-arrived");
// `secondary` is the muted "of N" companion (e.g. "of 38"). When
// primary === 0 (work finished) the card outlines green. Icon
// renders inside a colored circle — accent picks the bg color.
const TILE_FILTERS = { arr: ['remainingArrivals', 'arrived'], inh: ['inhouse'], dep: ['remainingDepartures', 'departed'], noroom: ['needsRoom'] };
const tileForFilter = (f) => Object.keys(TILE_FILTERS).find(k => TILE_FILTERS[k].includes(f)) || null;


// Sprint 17.8 — flattened reservation list w/ filter chips. Reads
// from `payload.reservations` (added in 17.7). Rendered when the
// view toggle is on "details".
// Sprint 18.1 — filter chips match the new mockup: All / Arrivals
// Today / In-house / Departures Today / Future / No Room Assigned.
// "Stayovers" chip dropped (overlap with In-house — staying-tonight
// surfaces as its own KPI card instead). "Future" + "No Room
// Assigned" are new.
// Sprint 20.3: tab definitions live in ./resnTabs.js (RESN_TABS).


// HK action implied by the reservation's kind. Mirrors what the
// per-room compute does — duplicated here so the table can show it
// per reservation row without needing to look up rooms.
const HK_ACTION_FOR_KIND = {
  arrival:   { label: 'None',       cls: 'none' },
  departure: { label: 'Full Clean', cls: 'full' },
  stayover:  { label: 'Touch-up',   cls: 'touch' },
  inhouse:   { label: 'None',       cls: 'none' },
  future:    { label: 'None',       cls: 'none' },
};

// Sprint 20.5: the reservations list (tabs, search, filters, cards, print/export) lives in ./ResnTable.js

// Sprint 17.8 — progress per cleaning category. Departure progress
// uses the metrics endpoint (remainingDepartures.remaining gives
// us "still to leave"; total - remaining = "already departed and
// presumably needing clean"). Stayovers + rooms-reviewed can't be
// tracked yet without a separate signal — show as 0% until we add
// that.
const ServiceProgress = ({ kpis, metricsSnapshot }) => {
  const depTotal = metricsSnapshot?.remainingDepartures?.total ?? kpis.departures ?? 0;
  const depRem   = metricsSnapshot?.remainingDepartures?.remaining ?? null;
  const depDone  = depRem != null ? (depTotal - depRem) : 0;
  const depPct   = depTotal > 0 ? Math.round((depDone / depTotal) * 100) : 0;

  // Stayover progress isn't trackable from current rGuest signals.
  // Placeholder — 0 of N until we wire up a per-room status check.
  const stayTotal = kpis.stayovers ?? 0;
  const stayDone  = 0;
  const stayPct   = stayTotal > 0 ? Math.round((stayDone / stayTotal) * 100) : 0;

  const Row = ({ label, done, total, pct, accent }) => (
    <div className={`fc-progress-row fc-progress-${accent}`}>
      <div className="fc-progress-meta">
        <div className="fc-progress-label">{label}</div>
        <div className="fc-progress-pct">{pct}%</div>
      </div>
      <div className="fc-progress-bar" aria-hidden="true">
        <div className="fc-progress-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="fc-progress-counts">
        <strong>{done}</strong> / {total}
      </div>
    </div>
  );

  return (
    <div className="fc-rail-card">
      <h3>Service Progress</h3>
      <Row label="Departure cleans"  done={depDone}  total={depTotal}  pct={depPct}  accent="dep"  />
      <Row label="Stayover touch-ups" done={stayDone} total={stayTotal} pct={stayPct} accent="stay" />
    </div>
  );
};

// Sprint 17.9 — raw payload viewer. Light modal that just pretty-
// prints `snapshot.payload` as JSON. Useful for FD/admin to verify
// what's coming from rGuest without diving into the History modal.
const RawOutputModal = ({ snapshot, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!snapshot) return null;
  const json = JSON.stringify(snapshot.payload, null, 2);
  const copy = async () => {
    try { await navigator.clipboard.writeText(json); } catch { /* noop */ }
  };
  return (
    <div className="fc-modal-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="fc-modal fc-modal-wide" onClick={(e) => e.stopPropagation()}>
        <header className="fc-modal-header">
          <h2>Raw scraper output</h2>
          <div className="fc-modal-header-actions">
            <button className="fc-modal-btn fc-modal-btn-small" onClick={copy}>Copy JSON</button>
            <button className="fc-modal-close" onClick={onClose} aria-label="Close">×</button>
          </div>
        </header>
        <div className="fc-modal-body">
          <pre className="fc-raw-pre">{json}</pre>
        </div>
      </div>
    </div>
  );
};

const ByCleaningTable = ({ rows }) => (
  <div className="fc-table-wrap">
    <table className="fc-table">
      <thead>
        <tr>
          <th>Cleaning Type</th>
          <th>Rooms Needed</th>
          <th>Avg Min / Room</th>
          <th>Housekeepers Needed</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.key}>
            <td>{r.name}</td>
            <td>{r.roomsNeeded}</td>
            <td>{r.avgMinPerRoom}</td>
            <td className="fc-table-emph">{r.housekeepersNeeded}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td>{rows.reduce((s, r) => s + r.roomsNeeded, 0)}</td>
          <td>—</td>
          <td className="fc-table-emph">
            {rows.reduce((s, r) => s + r.housekeepersNeeded, 0)}
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
);

const ByRoomTypeTable = ({ rows }) => (
  <div className="fc-table-wrap">
    <table className="fc-table">
      <thead>
        <tr>
          <th>Room Type</th>
          <th>Arrivals<br /><span className="fc-th-sub">(Check-ins)</span></th>
          <th>Departures<br /><span className="fc-th-sub">(Check-outs)</span></th>
          <th>Check-out Cleans<br /><span className="fc-th-sub">(Full)</span></th>
          <th>Stayover<br /><span className="fc-th-sub">(Touch-ups)</span></th>
          <th>Rooms Needed</th>
          <th>Housekeepers Needed</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.baseCode || r.baseLabel}>
            <td>{r.baseLabel}</td>
            <td>{r.arrivals}</td>
            <td>{r.departures}</td>
            <td>{r.checkoutCleans}</td>
            <td>{r.stayoverService}</td>
            <td>{r.roomsNeeded}</td>
            <td className="fc-table-emph">{r.housekeepersNeeded}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td>{rows.reduce((s, r) => s + r.arrivals, 0)}</td>
          <td>{rows.reduce((s, r) => s + r.departures, 0)}</td>
          <td>{rows.reduce((s, r) => s + r.checkoutCleans, 0)}</td>
          <td>{rows.reduce((s, r) => s + r.stayoverService, 0)}</td>
          <td>{rows.reduce((s, r) => s + r.roomsNeeded, 0)}</td>
          <td className="fc-table-emph">
            {rows.reduce((s, r) => s + r.housekeepersNeeded, 0)}
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
);

const ByFloorTable = ({ rows }) => {
  const [openFloor, setOpenFloor] = useState(null);
  return (
    <div className="fc-table-wrap">
      <table className="fc-table">
        <thead>
          <tr>
            <th>Floor</th>
            <th>Total Rooms</th>
            <th>Rooms to Clean</th>
            <th>Check-out Cleans</th>
            <th>Stayover</th>
            <th aria-label="expand"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => {
            const open = openFloor === r.floorId;
            return (
              <React.Fragment key={r.floorId || 'unknown'}>
                <tr
                  className={`fc-row-clickable${open ? ' open' : ''}`}
                  onClick={() => setOpenFloor(open ? null : r.floorId)}
                >
                  <td>{r.floorLabel}</td>
                  <td>{r.totalRooms}</td>
                  <td className="fc-table-emph">{r.roomsToClean}</td>
                  <td>{r.checkoutCleans}</td>
                  <td>{r.stayoverService}</td>
                  <td className="fc-row-caret">{open ? '▾' : '▸'}</td>
                </tr>
                {open && (
                  <tr className="fc-row-detail">
                    <td colSpan={6}>
                      <div className="fc-floor-detail">
                        <div className="fc-floor-detail-title">
                          Rooms on {r.floorLabel}
                        </div>
                        <ul className="fc-room-list">
                          {r.rooms.map(rm => (
                            <li key={rm.roomNumber}>
                              <span className="fc-room-num">{rm.roomNumber}</span>
                              <span className="fc-room-type">{rm.baseLabel || rm.typeCode || '?'}{rm.subLabel && rm.subLabel !== 'Standard' ? ` · ${rm.subLabel}` : ''}</span>
                              <span className={`fc-room-status fc-hk-${rm.hkStatus || 'unknown'}`}>
                                {rm.hkStatusLabel || '—'}
                              </span>
                              <span className={`fc-room-action fc-action-${rm.action}`}>
                                {ACTION_LABEL[rm.action] || rm.action}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr>
            <td>Total</td>
            <td>{rows.reduce((s, r) => s + r.totalRooms, 0)}</td>
            <td className="fc-table-emph">{rows.reduce((s, r) => s + r.roomsToClean, 0)}</td>
            <td>{rows.reduce((s, r) => s + r.checkoutCleans, 0)}</td>
            <td>{rows.reduce((s, r) => s + r.stayoverService, 0)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

const ScraperOutputCard = ({ snapshot }) => {
  const so = snapshot.payload?.scraperOutput || {};
  const status = snapshot.status === 'success' ? 'Success' : (snapshot.error_message || 'Failed');
  return (
    <div className="fc-rail-card">
      <div className="fc-rail-head">
        <h3>Scraper Output</h3>
        <span className={`fc-pill fc-pill-${snapshot.status}`}>{status}</span>
      </div>
      <div className="fc-rail-grid">
        <div>
          <div className="fc-rail-label">Source</div>
          <div className="fc-rail-value">{so.source || 'Agilysys rGuest Stay'}</div>
        </div>
        <div>
          <div className="fc-rail-label">Scraped at</div>
          <div className="fc-rail-value">{fmtTime(snapshot.scraped_at)}</div>
        </div>
        <div>
          <div className="fc-rail-label">Data window</div>
          <div className="fc-rail-value">{fmtDate(snapshot.forecast_date)}</div>
        </div>
        <div>
          <div className="fc-rail-label">Records processed</div>
          <div className="fc-rail-value">{snapshot.records_processed ?? so.recordsProcessed ?? 0}</div>
        </div>
      </div>
    </div>
  );
};

const DispatchSummaryCard = ({ data }) => (
  <div className="fc-rail-card">
    <h3>Dispatch Summary</h3>
    <ul className="fc-rail-list">
      <li>
        <span>Total rooms to service</span>
        <strong>{data?.totalRoomsToService ?? 0} rooms</strong>
      </li>
      <li>
        <span>Productivity target</span>
        <strong>{data?.productivityTarget ?? 0} rooms / attendant</strong>
      </li>
      <li>
        <span>Housekeepers needed</span>
        <strong>{data?.housekeepersNeeded ?? 0} attendants</strong>
      </li>
    </ul>
  </div>
);

const SendoutCard = ({ onClick, disabled, snapshot }) => (
  <div className="fc-rail-card">
    <div className="fc-rail-head">
      <h3>Housekeeping Send-out</h3>
      <span className="fc-pill fc-pill-ready">Ready to send</span>
    </div>
    <div className="fc-rail-grid">
      <div>
        <div className="fc-rail-label">Forecast date</div>
        <div className="fc-rail-value">{fmtDate(snapshot.forecast_date)}</div>
      </div>
      <div>
        <div className="fc-rail-label">Generated</div>
        <div className="fc-rail-value">{fmtTime(snapshot.scraped_at)}</div>
      </div>
    </div>
    <button
      className="fc-btn fc-btn-primary fc-rail-cta"
      onClick={onClick}
      disabled={disabled}
      title={disabled ? 'Run the scraper first' : 'Open a printable forecast sheet'}
    >
      ▸ Generate forecast
    </button>
  </div>
);

const DonutLegend = ({ rows, total }) => (
  <div className="fc-donut-card">
    <h3>Rooms Needed by Cleaning Type</h3>
    <div className="fc-donut-body">
      <div className="fc-donut-total">
        <div className="fc-donut-number">{total}</div>
        <div className="fc-donut-sublabel">Total</div>
      </div>
      <ul className="fc-donut-legend">
        {rows.map(r => {
          const pct = total > 0 ? Math.round((r.roomsNeeded / total) * 100) : 0;
          return (
            <li key={r.key}>
              <span className={`fc-donut-dot fc-donut-${r.key}`} aria-hidden="true" />
              <span className="fc-donut-name">{r.name}</span>
              <span className="fc-donut-count">{r.roomsNeeded} ({pct}%)</span>
            </li>
          );
        })}
      </ul>
    </div>
  </div>
);


// ── Page ───────────────────────────────────────────────────

// Sprint 22.4: `mode` is fixed per surface (no toggle): the manager shell renders 'manager' (default), the Dev shell renders 'dev', which keeps
// every debugging / settings control (snapshot history, forecast settings, raw scraper output, guest-detail job bar).
const Forecasting = ({ mode = 'manager' }) => {
  const isDev = mode === 'dev';
  const { goTo } = useView(); // Sprint 17.10 — back-to-Home button
  const [snapshot, setSnapshot] = useState(null);
  const [dataSource, setDataSource] = useState(null);   // Sprint 22.3: 'mirror' (auto-sync) | 'snapshot' (last stored scrape)
  const [syncHealth, setSyncHealth] = useState(null);   // Sprint 22.4: plain freshness state from the server
  const [loading, setLoading]   = useState(true);
  const [scraping, setScraping] = useState(false);
  const [error, setError]       = useState(null);
  const [view, setView]         = useState('details'); // 'cleaning' | 'room' | 'floor' | 'details' (17.8 default)
  // Sprint 20.3: opens on Remaining arrivals. Sprint 20.5: remembers the last tab for this browser session.
  const [resnFilter, setResnFilter]     = useState(() => {
    try { const t = sessionStorage.getItem('hotelops-resn-tab'); if (RESN_TABS.some(x => x.key === t)) return t; } catch { /* storage unavailable */ }
    return DEFAULT_RESN_TAB;
  });
  useEffect(() => { try { sessionStorage.setItem('hotelops-resn-tab', resnFilter); } catch { /* ignore */ } }, [resnFilter]);
  const [resnSourceFilter, setResnSourceFilter] = useState(null);
  const [resnTypeFilter, setResnTypeFilter]     = useState(null);
  // sheetOpen state removed in 17.12 (Generate Forecast moved off this page).
  const [settingsOpen, setSettingsOpen] = useState(false); // Sprint 17.5
  const [historyOpen, setHistoryOpen]   = useState(false); // Sprint 17.5
  const [rawOpen, setRawOpen]           = useState(false); // Sprint 17.9
  const [scrapePct, setScrapePct]       = useState(0);     // 17.9 progress ring
  // Sprint 20.3 — background guest-detail job (Sprint 20.2): progress shown under the title.
  const [detailJob, setDetailJob]       = useState(null);
  // Sprint 22.8 — which tile is selected. Set on the click itself (so the highlight is instant and never depends on a tab that
  // may be hidden when its count is 0); a manual tab change in the table re-syncs it.
  const [tile, setTile] = useState(() => tileForFilter(resnFilter));
  const roomPanel = tile === 'left' || tile === 'dirty' ? tile : null;   // the room lists replace the table inside the same card
  const pickFilter = (f) => { setResnFilter(f); setTile(tileForFilter(f)); };

  const loadLatest = useCallback(async () => {
    setError(null);
    // Sprint 22.3: the auto-sync mirror when it is ready (falls back to the last stored snapshot server-side).
    const { ok, data } = await apiFetch('/admin/forecast/current');
    if (!ok || !data?.success) {
      setError(data?.message || 'Could not load latest forecast.');
      setLoading(false);
      return;
    }
    setSnapshot(data.snapshot || null);
    setDataSource(data.source || null);
    setSyncHealth(data.sync || null);
    setLoading(false);
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);

  // Sprint 20.3 — job progress. Fetch once on mount (so a job started in
  // another tab/earlier shows), then poll every 2.5 s ONLY while it is
  // running and the tab is visible. Idle page = zero polling (compute-cost
  // rule from Sprint 19.1).
  const jobRunning = detailJob?.status === 'running';
  const fetchJob = useCallback(async () => {
    const { ok, data } = await apiFetch('/admin/forecast/jobs/latest');
    if (ok && data?.success) setDetailJob(data.job || null);
  }, []);
  useEffect(() => { fetchJob(); }, [fetchJob]);
  useEffect(() => {
    if (!jobRunning) return undefined;
    const id = setInterval(() => { if (document.visibilityState === 'visible') fetchJob(); }, 2500);
    return () => clearInterval(id);
  }, [jobRunning, fetchJob]);

  // Sprint 22.3: with the auto-sync mirror, "refresh" = ask the sync engine for a quick scan + hourly check (seconds, no full scrape),
  // wait for it (the status endpoint is DB-free), then re-read. Without a ready mirror it is the old full scrape.
  const refreshFromSync = async () => {
    const start = await apiFetch('/admin/sync/run', { method: 'POST', body: JSON.stringify({ kind: 'refresh' }) });
    if (!start.ok || !start.data?.success) throw new Error(start.data?.message || 'Could not start the refresh.');
    if (start.data.started !== false) {
      const t0 = Date.now();
      for (;;) {
        await new Promise(r => setTimeout(r, 1500));
        const st = await apiFetch('/admin/sync/health');      // managers can't read the full sync status (dev-only); health carries `running`
        if (st.ok && st.data?.success && !st.data.health.running) break;
        if (Date.now() - t0 > 120000) break;           // never wait forever; whatever finished is shown
      }
    }
    await loadLatest();
  };

  const handleScrape = async () => {
    setScraping(true);
    setError(null);
    setScrapePct(2);
    if (dataSource === 'mirror') {
      try { await refreshFromSync(); } catch (e) { setError(e.message || 'Refresh failed.'); }
      setScrapePct(100); setScraping(false);
      return;
    }
    const { ok, data } = await apiFetch('/admin/forecast/scrape', {
      method: 'POST',
      body:   JSON.stringify({}),
    });
    // Snap to 100% on completion regardless of where the fake
    // timer landed, then let the effect clear it.
    setScrapePct(100);
    setScraping(false);
    if (!ok || !data?.success) {
      setError(data?.message || 'Scrape failed. Check Agilysys credentials + the snapshot logs.');
      return;
    }
    setSnapshot(data.snapshot);
    if (data.detailJob?.job) setDetailJob(data.detailJob.job);
  };

  // Sprint 17.9 — faux progress timer. Backend doesn't stream
  // per-step progress, so we approximate. Live scrapes empirically
  // take 10–18 s (login + 4 parallel calls + DB upsert). Ease the
  // ring toward 95% over ~14 s; when the request completes,
  // handleScrape snaps it to 100% and this effect drops it back to
  // 0 after a short rest.
  useEffect(() => {
    if (!scraping) {
      if (scrapePct !== 0) {
        const t = setTimeout(() => setScrapePct(0), 700);
        return () => clearTimeout(t);
      }
      return undefined;
    }
    const TICK_MS  = 200;
    const TARGET   = 95;
    const DURATION = 14000; // ms — feels about right empirically
    const startedAt = Date.now();
    const id = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      const ratio   = Math.min(1, elapsed / DURATION);
      // Ease-out so the ring slows visibly as it nears 95% (avoids
      // the "appears stalled at 100%" feel).
      const eased = 1 - Math.pow(1 - ratio, 1.8);
      setScrapePct(Math.min(TARGET, Math.round(eased * TARGET)));
    }, TICK_MS);
    return () => clearInterval(id);
    // scrapePct intentionally not in deps — that's the value we set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scraping]);

  // Sprint 17.4: open the printable forecast sheet over the page.
  // We keep it as an in-page modal (rather than a new tab) so the
  // print stylesheet can guarantee what reaches paper.
  // handleGenerate / generateDisabled removed in 17.12 — see Forecast/.

  const condition = useMemo(() => (snapshot && snapshot.payload ? buildRoomCondition(snapshot.payload) : null), [snapshot]);   // Sprint 22.7
  const lastSync = snapshot ? fmtTime(snapshot.scraped_at) : '—';
  const kpis = snapshot?.payload?.kpis || {};

  // Memoized derived lists for the Reservation Details filter dropdowns.
  const detailSources = useMemo(() => {
    if (!snapshot?.payload?.reservations) return [];
    const set = new Set();
    for (const r of snapshot.payload.reservations) if (r.source) set.add(r.source);
    return [...set].sort();
  }, [snapshot]);
  const detailRoomTypes = useMemo(() => {
    if (!snapshot?.payload?.reservations) return [];
    const set = new Set();
    for (const r of snapshot.payload.reservations) if (r.baseLabel) set.add(r.baseLabel);
    return [...set].sort();
  }, [snapshot]);

  const tableEl = useMemo(() => {
    if (!snapshot?.payload) return null;
    if (view === 'details') {
      return (
        <ResnTable
          rows={snapshot.payload.reservations || []}
          filter={resnFilter}
          onFilter={pickFilter}
          sources={detailSources}
          roomTypes={detailRoomTypes}
          sourceFilter={resnSourceFilter}
          onSourceFilter={setResnSourceFilter}
          typeFilter={resnTypeFilter}
          onTypeFilter={setResnTypeFilter}
          // Coarse tick (every 10 reservations) so a big view doesn't re-download on every progress poll.
          jobTick={`${detailJob?.status || ''}:${Math.floor(((detailJob?.done || 0) + (detailJob?.failed || 0)) / 10)}`}
          todayYmd={snapshot.payload.forecastDate}
          jobRunning={detailJob?.status === 'running'}
          lean={!isDev}
        />
      );
    }
    if (view === 'cleaning') return <ByCleaningTable rows={snapshot.payload.byCleaningType || []} />;
    if (view === 'room')     return <ByRoomTypeTable rows={snapshot.payload.byRoomType    || []} />;
    if (view === 'floor')    return <ByFloorTable    rows={snapshot.payload.byFloor       || []} />;
    return null;
  }, [snapshot, view, resnFilter, resnSourceFilter, resnTypeFilter, detailSources, detailRoomTypes, detailJob?.status, detailJob?.done, detailJob?.failed, isDev]);

  return (
    <div className="fc-page">
      <header className="fc-header">
        <div className="fc-header-text">
          {/* Sprint 17.10 — quick back to admin Home. Redundant
              with the sidebar Home button on desktop but matches
              the mobile mockup pattern (top-left chevron). */}
          <button
            type="button"
            className="fc-back-btn"
            onClick={() => goTo('home')}
            aria-label="Back to Home"
          >
            <IconBack /> <span>Home</span>
          </button>
          {/* Sprint 20.1 — title, last-sync badge and the icon-only refresh
              share ONE row at every width (phone included). The old
              "Run scraper" text button (154 px min-width) forced the
              actions onto their own line below the title. */}
          <div className="fc-title-row">
            <h1>Reservations</h1>
            <div className="fc-header-actions">
              <SyncBadge source={dataSource} syncedAt={snapshot ? snapshot.scraped_at : null} health={syncHealth} label="" />
              <button
                type="button"
                className="fc-btn fc-btn-primary fc-btn-icon"
                onClick={handleScrape}
                disabled={scraping}
                title={scraping ? `Running… ${scrapePct}%` : (dataSource === 'mirror' ? 'Refresh — check rGuest for changes now' : 'Refresh — run the rGuest scraper')}
                aria-label={scraping ? `Refreshing, ${scrapePct} percent` : (dataSource === 'mirror' ? 'Refresh: check rGuest for changes' : 'Refresh: run the rGuest scraper')}
              >
                {scraping ? <ProgressRing pct={scrapePct} /> : <IconRefresh />}
              </button>
            </div>
          </div>
          {/* Sprint 17.9 — subtitle removed (was descriptive only);
              the three meta links carry the actionable affordances. */}
          {isDev && (
          <div className="fc-header-meta-actions">
            <button
              type="button"
              className="fc-meta-link"
              onClick={() => setHistoryOpen(true)}
            >
              <IconClock />
              <span>Snapshot history</span>
            </button>
            <button
              type="button"
              className="fc-meta-link"
              onClick={() => setSettingsOpen(true)}
            >
              <IconGear />
              <span>Forecast settings</span>
            </button>
            <button
              type="button"
              className="fc-meta-link"
              onClick={() => setRawOpen(true)}
              disabled={!snapshot}
              title={!snapshot ? 'Run the scraper first' : 'View raw payload as JSON'}
            >
              <IconDocument />
              <span>Raw scraper output</span>
            </button>
          </div>
          )}
        </div>
      </header>

      {/* Sprint 20.3 — guest-detail job (Sprint 20.2) progress; only while running or when it needs attention. */}
      {isDev && detailJob && (detailJob.status === 'running' || detailJob.status === 'failed' || detailJob.status === 'partial' || detailJob.status === 'interrupted') && (
        <div className={`fc-jobbar fc-jobbar-${detailJob.status}`} role="status" aria-live="polite">
          {detailJob.status === 'running' ? (
            <>
              <span>Loading guest details <strong>{detailJob.done + detailJob.failed} / {detailJob.total}</strong></span>
              <span className="fc-jobbar-track" aria-hidden="true">
                <span className="fc-jobbar-fill" style={{ width: `${detailJob.total ? Math.min(100, Math.round(((detailJob.done + detailJob.failed) / detailJob.total) * 100)) : 0}%` }} />
              </span>
            </>
          ) : detailJob.status === 'partial' ? (
            <span>Guest details loaded, but {detailJob.failed} reservation{detailJob.failed === 1 ? '' : 's'} couldn't be refreshed — the last known data is shown.</span>
          ) : (
            <span>Guest details couldn't finish loading{detailJob.error ? `: ${detailJob.error}` : '.'} Refresh to retry.</span>
          )}
        </div>
      )}

      {snapshot?.payload && snapshot.payload.reservationsComplete === false && (
        <div className="fc-jobbar fc-jobbar-partial" role="alert">
          The reservation list may be incomplete (rGuest didn't return every record). Refresh to try again.
        </div>
      )}

      {loading && (
        <div className="fc-loading">Loading latest forecast…</div>
      )}

      {error && (
        <div className="fc-error" role="alert">
          <strong>Something went wrong.</strong> {error}
        </div>
      )}

      {!loading && !snapshot && !error && (
        <div className="fc-empty">
          <h2>No forecast yet</h2>
          <p>Tap the <strong>refresh</strong> button above to pull today's data from rGuest Stay and generate the first forecast.</p>
        </div>
      )}

      {snapshot && (
        <>
          <section className={`fc-kpis fc-kpis-${isDev ? 6 : 5}`} aria-label="Reservations KPIs">
            {(() => {
              // Sprint 22.7 — the tiles lead with what is LEFT to do (arrivals still to check in, check-outs still to leave), each with a status ring
              // and a count-up animation; "Staying tonight" (it duplicated In-house) is gone; two room tiles (left to sell / dirty + occupied clean) open
              // room lists underneath. Numbers come from the same payload as the lists below.
              const arrTotal = kpis.arrivals ?? 0, arrLeft = kpis.remainingArrivals ?? 0, arrived = Math.max(0, arrTotal - arrLeft);
              const depTotal = kpis.departures ?? 0, depLeft = kpis.remainingDepartures ?? 0, checkedOut = Math.max(0, depTotal - depLeft);
              const reservations = snapshot.payload.reservations || [];
              // Sprint 20.3: same definition as the "Needs a room" tab.
              const noRoomCount  = reservations.filter(tabPredicate('needsRoom')).length;
              const goTab = (key, name) => { setResnFilter(key); setTile(name); };
              const cond = condition, cc = cond ? cond.counts : null;
              return (
                <>
                  <KpiCard
                    accent="arrivals"
                    icon={<IconBriefcase />}
                    label="Arrivals left"
                    primary={arrLeft}
                    sublabel={`${arrived} arrived of ${arrTotal} today`}
                    ring={fraction(arrived, arrTotal)} ringColor="#6b46c1" ringLabel={`${arrived} of ${arrTotal} arrived`}
                    onClick={() => goTab('remainingArrivals', 'arr')}
                    active={tile === 'arr'}
                  />
                  <KpiCard
                    accent="inhouse"
                    icon={<IconBed />}
                    label="In-house"
                    primary={kpis.inHouse ?? 0}
                    sublabel="guests currently staying"
                    ring={fraction(kpis.inHouse ?? 0, cond ? cond.sellable : 0)} ringColor="#2b6cb0" ringLabel={cond ? `${kpis.inHouse ?? 0} of ${cond.sellable} rooms occupied` : undefined}
                    onClick={() => goTab('inhouse', 'inh')}
                    active={tile === 'inh'}
                  />
                  <KpiCard
                    accent="departures"
                    icon={<IconExit />}
                    label="Check-outs left"
                    primary={depLeft}
                    sublabel={`${checkedOut} checked out of ${depTotal} today`}
                    ring={fraction(checkedOut, depTotal)} ringColor="#dd6b20" ringLabel={`${checkedOut} of ${depTotal} checked out`}
                    onClick={() => goTab('remainingDepartures', 'dep')}
                    active={tile === 'dep'}
                  />
                  {cc && (
                    <KpiCard
                      accent="staying"
                      icon={<IconSparkle />}
                      label="Left to sell"
                      primary={cc.leftToSell}
                      sublabel={`${cc.vi} inspected · ${cc.pu} pickup`}
                      ring={fraction(cc.leftToSell, cond.sellable)} ringColor="#2f855a" ringLabel={`${cc.leftToSell} of ${cond.sellable} rooms left to sell`}
                      onClick={() => setTile(t => (t === 'left' ? tileForFilter(resnFilter) : 'left'))}
                      active={tile === 'left'}
                    />
                  )}
                  {cc && (
                    <KpiCard
                      accent="dirty"
                      icon={<IconBroom />}
                      label="Dirty rooms"
                      primary={cc.dirtyTotal}
                      sublabel={`${cc.dirty} dirty · ${cc.inProgress} in progress · ${cc.occupiedClean} occupied clean`}
                      ring={fraction(cc.dirtyTotal, cond.total)} ringColor="#c53030" ringLabel={`${cc.dirtyTotal} of ${cond.total} rooms need cleaning`}
                      onClick={() => setTile(t => (t === 'dirty' ? tileForFilter(resnFilter) : 'dirty'))}
                      active={tile === 'dirty'}
                    />
                  )}
                  {isDev && (
                    <KpiCard
                      accent="noroom"
                      icon={<IconAlertTriangle />}
                      label="No Room Assigned"
                      primary={noRoomCount}
                      sublabel="needs review"
                      onClick={() => goTab('needsRoom', 'noroom')}
                      active={tile === 'noroom'}
                    />
                  )}
                </>
              );
            })()}
          </section>
          <div className={`fc-body${isDev ? ' fc-body-dev' : ''}`}>
            <main className="fc-main">
              {/* Sprint 22.8 — the five tiles all work the same way: they switch what THIS card shows (guest tabs for the first three,
                  room lists for Left to sell / Dirty rooms). */}
              {roomPanel && condition ? (
                <RoomConditionPanel kind={roomPanel} condition={condition} mode={mode} embedded onClose={() => setTile(tileForFilter(resnFilter))} />
              ) : (
                <>
                  <div className="fc-table-header">
                    <h2>Guest Reservations</h2>
                  </div>
                  {tableEl}
                </>
              )}
            </main>
            {/* Sprint 22.8 — dev: what the auto-sync saw change, as a small card on the right (like the dev Forecast page). */}
            {isDev && <aside className="fc-side"><RecentChanges /></aside>}
            {/* Sprint 20.4: the right rail (Today at a glance + Selected reservation) is gone — the KPI tiles above
                are the glance, and details expand in place on each card. */}
          </div>

        </>
      )}

      {/* Sprint 17.12: ForecastSheet modal moved to the Forecast
          page. The page's SendoutCard is also gone. */}

      {isDev && settingsOpen && (
        <ForecastSettings
          onClose={() => setSettingsOpen(false)}
        />
      )}

      {isDev && historyOpen && (
        <ForecastHistory
          onClose={() => setHistoryOpen(false)}
        />
      )}

      {isDev && rawOpen && (
        <RawOutputModal
          snapshot={snapshot}
          onClose={() => setRawOpen(false)}
        />
      )}
    </div>
  );
};

// Sprint 24: the front-desk Home reuses the same tile + icons (no copy, so the two can't drift apart).
export { KpiCard, IconBriefcase, IconBed, IconExit, IconSparkle, IconBroom };
export default Forecasting;
